import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { 
  MessageSquare, 
  Star, 
  Send, 
  Check, 
  Bug, 
  Lightbulb, 
  ThumbsUp,
  HelpCircle,
  Zap,
  ChevronRight
} from "lucide-react";

const feedbackCategories = [
  { 
    id: "bug", 
    icon: Bug, 
    label: "Report a Bug", 
    color: "bg-red-500/20 text-red-400",
    description: "Something isn't working correctly"
  },
  { 
    id: "feature", 
    icon: Lightbulb, 
    label: "Feature Request", 
    color: "bg-yellow-500/20 text-yellow-400",
    description: "Suggest a new feature or improvement"
  },
  { 
    id: "praise", 
    icon: ThumbsUp, 
    label: "General Feedback", 
    color: "bg-green-500/20 text-green-400",
    description: "Share what you love about Viraly"
  },
  { 
    id: "question", 
    icon: HelpCircle, 
    label: "Question", 
    color: "bg-blue-500/20 text-blue-400",
    description: "Need help with something?"
  }
];

const urgencyLevels = [
  { id: "low", label: "Low", color: "bg-gray-500" },
  { id: "medium", label: "Medium", color: "bg-yellow-500" },
  { id: "high", label: "High", color: "bg-orange-500" },
  { id: "critical", label: "Critical", color: "bg-red-500" }
];

export default function FeedbackPage() {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [urgency, setUrgency] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleCategorySelect = (cat) => {
    setCategory(cat);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error("Please enter your feedback");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(`/feedback?message=${encodeURIComponent(`[${category?.label}] ${message}`)}&rating=${rating || ''}`);
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (error) {
      toast.error("Failed to submit feedback");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-8 max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card-base p-12 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-viraly-success/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-viraly-success" />
            </div>
            <h2 className="font-heading text-3xl font-bold mb-3">Thank You!</h2>
            <p className="text-viraly-muted mb-2">
              Your feedback has been received.
            </p>
            <p className="text-sm text-viraly-muted mb-8">
              We read every submission and use it to make Viraly better.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setStep(1);
                  setCategory(null);
                  setMessage("");
                  setRating(0);
                }}
                className="btn-secondary"
              >
                Submit More Feedback
              </Button>
              <Button
                onClick={() => window.history.back()}
                className="btn-primary"
              >
                Back to Dashboard
              </Button>
            </div>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-viraly-orange" />
            Send Feedback
          </h1>
          <p className="text-viraly-muted">
            Help us improve Viraly. We read every piece of feedback.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center gap-2 mb-8">
          <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-viraly-orange' : 'bg-white/20'}`} />
          <div className={`flex-1 h-0.5 ${step >= 2 ? 'bg-viraly-orange' : 'bg-white/20'}`} />
          <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-viraly-orange' : 'bg-white/20'}`} />
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Choose Category */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <h2 className="font-heading text-xl font-bold mb-4">What type of feedback?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {feedbackCategories.map((cat, i) => (
                  <motion.button
                    key={cat.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    onClick={() => handleCategorySelect(cat)}
                    className="card-base p-5 text-left hover:border-viraly-orange/30 transition-all group"
                    data-testid={`feedback-category-${cat.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cat.color}`}>
                        <cat.icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold mb-1 group-hover:text-viraly-orange transition-colors">
                          {cat.label}
                        </h3>
                        <p className="text-sm text-viraly-muted">{cat.description}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-viraly-muted group-hover:text-viraly-orange transition-colors" />
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Write Feedback */}
          {step === 2 && category && (
            <motion.form
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              onSubmit={handleSubmit}
            >
              {/* Back Button */}
              <button
                type="button"
                onClick={() => { setStep(1); setCategory(null); }}
                className="text-viraly-muted hover:text-white text-sm mb-6 flex items-center gap-2"
              >
                ← Back to categories
              </button>

              {/* Selected Category */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${category.color} mb-6`}>
                <category.icon className="w-4 h-4" />
                <span className="font-medium">{category.label}</span>
              </div>

              <div className="space-y-6">
                {/* Rating (only for praise/general) */}
                {(category.id === "praise" || category.id === "question") && (
                  <div className="card-base p-5">
                    <Label className="mb-3 block">How's your experience so far?</Label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          className="p-1 transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-8 h-8 transition-colors ${
                              star <= (hoverRating || rating)
                                ? "text-yellow-400 fill-yellow-400"
                                : "text-white/20"
                            }`}
                          />
                        </button>
                      ))}
                      {rating > 0 && (
                        <span className="ml-3 text-sm text-viraly-muted">
                          {rating === 5 ? "Amazing! 🎉" : rating === 4 ? "Great!" : rating === 3 ? "Good" : rating === 2 ? "Okay" : "Needs work"}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Urgency (only for bugs) */}
                {category.id === "bug" && (
                  <div className="card-base p-5">
                    <Label className="mb-3 block">How urgent is this?</Label>
                    <div className="flex flex-wrap gap-2">
                      {urgencyLevels.map((level) => (
                        <button
                          key={level.id}
                          type="button"
                          onClick={() => setUrgency(level.id)}
                          className={`px-4 py-2 rounded-full border transition-all flex items-center gap-2 ${
                            urgency === level.id
                              ? "border-viraly-orange bg-viraly-orange/10"
                              : "border-white/10 hover:border-white/20"
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${level.color}`} />
                          {level.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Message */}
                <div className="card-base p-5">
                  <Label className="mb-3 block">
                    {category.id === "bug" ? "Describe the issue" :
                     category.id === "feature" ? "Describe your idea" :
                     category.id === "question" ? "What do you need help with?" :
                     "Your feedback"}
                  </Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      category.id === "bug" ? "What happened? What did you expect to happen? Steps to reproduce..." :
                      category.id === "feature" ? "Describe the feature you'd like to see..." :
                      category.id === "question" ? "What would you like to know?" :
                      "Tell us what you think..."
                    }
                    className="input-base min-h-[150px]"
                    data-testid="feedback-textarea"
                  />
                  <p className="text-xs text-viraly-muted mt-2">
                    {message.length}/1000 characters
                  </p>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading || !message.trim()}
                  className="btn-primary w-full py-4"
                  data-testid="submit-feedback-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Submit Feedback
                    </span>
                  )}
                </Button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center text-sm text-viraly-muted"
        >
          <p>
            Need urgent help? Email us at{" "}
            <button onClick={() => window.dispatchEvent(new CustomEvent('openViraChat'))} className="text-viraly-orange hover:underline">
              Chat with Vira
            </button>
          </p>
          <p className="mt-1 text-xs">Support hours: Mon-Fri 9AM-5PM EST</p>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
